import { WhatsAppMessage } from 'types';

/**
 * Dating and timing a chat thread, and building the row for a message we have
 * just sent.
 *
 * Extracted from WhatsAppChats.tsx so the rules are testable — the page itself
 * pulls in the whole API layer.
 */

/** Which calendar day a message belongs to. '' when it has no timestamp. */
export const dayKey = (ts: number | undefined): string =>
  ts ? new Date(ts).toDateString() : '';

/**
 * How that day is written between messages.
 *
 * The bubble carried toLocaleTimeString() alone, so every message read as a
 * bare "4:12:07 pm" with nothing to say which day it was: a thread going back
 * weeks looked like it all happened this afternoon.
 */
export const dayLabel = (ts: number | undefined, now: number = Date.now()): string => {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';

  // Inside the last week the weekday alone reads best; past that it needs the
  // date, and a different year needs the year with it.
  if (ts >= now - 6 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
};

/** Time on the bubble. The full stamp goes in its title attribute. */
export const messageTime = (ts: number | undefined): string =>
  ts ? new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : 'Pending';

/**
 * A message we have just sent, as the thread needs it.
 *
 * POST /whatsapp/messages/send answers `{ messageId }` and nothing else — not
 * the stored row. The three send handlers used to append that object straight
 * into the thread, so the bubble had no `direction` (it drew on the left, as
 * though the contact had sent it), no `content` (so it fell through to the
 * italic "unsupported type" line) and no `sentAt` (so it read "Pending"). What
 * the sender saw was a near-blank bubble on the wrong side, which is why a sent
 * message looked lost until a reload fetched the real row.
 *
 * Build the row from what we already know; the caller reconciles against the
 * server straight after for the parts we don't — the database id, and the
 * delivery status as it ticks.
 */
export const localSentMessage = (
  messageId: string | undefined,
  type: WhatsAppMessage['type'],
  fields: Partial<WhatsAppMessage> = {},
  now: number = Date.now(),
): WhatsAppMessage =>
  ({
    // Only used as a React key until the refetch lands, but it has to be
    // unique: two sends in the same tick with no id would collide.
    id: messageId || `pending-${now}-${Math.random().toString(36).slice(2, 8)}`,
    messageId,
    direction: 'OUTBOUND',
    status: 'SENT',
    type,
    sentAt: now,
    ...fields,
  }) as WhatsAppMessage;
